import { useState, useEffect, useCallback, useRef } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

export const PAGE_SECTION_MAP = {
  '/admin':                       'overview',
  '/admin/analytics':             'overview',
  '/admin/users':                 'users',
  '/admin/investments':           'investments',
  '/admin/vip':                   'investments',
  '/admin/referrals':             'referrals',
  '/admin/recharge':              'money',
  '/admin/withdrawals':           'money',
  '/admin/refunds':               'money',
  '/admin/spin-wheel':            'engagement',
  '/admin/scratch-card':          'engagement',
  '/admin/streak':                'engagement',
  '/admin/tasks':                 'engagement',
  '/admin/leaderboard':           'engagement',
  '/admin/leaderboard-rewards':   'engagement',
  '/admin/banners':               'content',
  '/admin/promotions':            'content',
  '/admin/bonus-codes':           'content',
  '/admin/user-reviews':          'content',
  '/admin/help-center':           'content',
  '/admin/support':               'support',
  '/admin/bug-reports':           'support',
  '/admin/push-notifications':    'notifications',
  '/admin/settings':              'system',
  '/admin/terms':                 'system',
  '/admin/monitoring':            'system',
  '/admin/audit-logs':            'audit',
  '/admin/fraud-alerts':          'users',
};

export const PAGE_ROLE_MAP = {
  '/admin/roles': ['super_admin'],
  '/admin/appearance': ['super_admin'],
};

export const SECTION_LABELS = {
  overview:     'Overview / Dashboard',
  money:        'Money (Recharge, Withdrawals, Refunds)',
  users:        'Users (User Management)',
  investments:  'Investments & VIP (Plans, VIP Levels)',
  referrals:    'Referrals',
  support:      'Support Tickets',
  engagement:   'Engagement (Spin, Scratch Card, Streak, Tasks, Leaderboard)',
  content:      'Content (Banners, Promotions, Bonus Codes, Help Center, User Reviews)',
  system:       'System (Settings, Monitoring, Device Verification)',
  notifications: 'Push Notifications',
  audit:        'Audit Logs',
};

export const ALL_SECTIONS = Object.keys(SECTION_LABELS);
const FULL_ACCESS_ROLES = ['super_admin', 'admin'];

// Module-level cache — persists across re-renders, cleared on logout
let _cache = null;
const CACHE_TTL = 60 * 1000; // 60s — reduced so perm changes apply quickly

export function clearPermCache() { _cache = null; }

export function usePermissions() {
  const { user } = useAuth();
  const isFullAccess = FULL_ACCESS_ROLES.includes(user?.role);
  const fetchingRef = useRef(false);

  const getCached = () => {
    if (isFullAccess) return _buildFullPerms();
    if (_cache && _cache.role === user?.role && Date.now() - _cache.fetchedAt < CACHE_TTL) {
      return _cache.permissions;
    }
    return null;
  };

  const [permissions, setPermissions] = useState(getCached);
  // loading = true only if we don't have perms yet and need to fetch
  const [loading, setLoading] = useState(() => !getCached());

  useEffect(() => {
    if (!user) { setPermissions(null); setLoading(false); return; }

    // Full-access roles never need API call
    if (isFullAccess) {
      setPermissions(_buildFullPerms());
      setLoading(false);
      return;
    }

    // Cache hit
    const cached = getCached();
    if (cached) {
      setPermissions(cached);
      setLoading(false);
      return;
    }

    // Need to fetch
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    setLoading(true);

    api.get('/admin-roles/my-permissions')
      .then(r => {
        const perms = r.data.permissions || {};
        _cache = { permissions: perms, role: user.role, fetchedAt: Date.now() };
        setPermissions(perms);
      })
      .catch(() => {
        setPermissions({});
      })
      .finally(() => {
        setLoading(false);
        fetchingRef.current = false;
      });
    // Only re-run when user role changes
  }, [user?.role]); // eslint-disable-line

  // Stable `can` function - won't cause re-renders in dependents
  const can = useCallback((section, action = 'can_view') => {
    if (!user) return false;
    if (isFullAccess) return true;
    if (!permissions) return false;
    return permissions[section]?.[action] === true;
  }, [user, isFullAccess, permissions]); // permissions is stable once set

  const canEdit = useCallback((s) => can(s, 'can_edit'), [can]);
  const canDelete = useCallback((s) => can(s, 'can_delete'), [can]);
  const canAccessPath = useCallback((path) => {
    const allowedRoles = PAGE_ROLE_MAP[path];
    if (allowedRoles && !allowedRoles.includes(user?.role)) return false;
    const section = PAGE_SECTION_MAP[path];
    return !section || can(section);
  }, [can, user?.role]);

  return { permissions, loading, can, canEdit, canDelete, canAccessPath };
}

function _buildFullPerms() {
  const p = {};
  ALL_SECTIONS.forEach(s => { p[s] = { can_view: true, can_edit: true, can_delete: true }; });
  return p;
}
